/*
 * Project: bellcloud
 * File: apps/frontend/src/connections/ble/useBle.tsx
 * Author: SWAN
 * Created: 2025-01-27 10:00:00
 * Description: Web BLE Provider - 기기 연결 관리
 */

import { createContext, useContext, useRef, useState } from 'react';
import type { PropsWithChildren } from 'react';
import type {
  GeneratePacketRequest,
  PacketToJsonResponse,
} from '@packages/types/device/common/api.common';
import type { ConnectedDeviceInfo } from '@packages/types/device/common/deviceTypes';
import { DeviceType, detectDeviceTypeFromName } from '@packages/types/device/common/deviceTypes';

import { WebBleAdapter } from './bleAdapter';
import { useGeneratePacket, useParsePacket } from '@/api/device/useLite';
import { canSyncPacketToCloud } from '@/utils/cloudSyncAuth';
import { useGenerateMiniPacket, useParseMiniPacket } from '@/api/device/useMini';
import { SinglePacketMode, BurstMode } from '../modes';
import type { PacketApiAdapter } from '../modes/types';

// (주의) useRef는 함수 컴포넌트 내부에서만 호출해야 함
import type { DeviceInfo } from '@packages/types/device/lite/deviceInfo';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { I18nError, getI18nKey } from '@packages/types/errors';
import { apiHelpers } from '@/api/core';
import { useAppStore } from '@/store/loadingStore';
import {
  LogDownloadHandler,
  type LogDownloadRequest,
  type LogDownloadResult,
} from '@/utils/logDownloadHandler';

// BLE 연결 상태 타입
type BleConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

// BLE 디바이스 정보 타입
interface BleDeviceInfo {
  name: string;
  address: string;
}

interface BleContextValue {
  canUseBle: boolean;
  hasTriedAutoconnect: boolean;
  connectionState: BleConnectionState;
  error?: string;
  connectedDevice?: ConnectedDeviceInfo;
  connect(): Promise<boolean>;
  disconnect(): void;
  scan(): Promise<BleDeviceInfo[]>;
  scanAndConnect(deviceFilter?: DeviceType | 'bell', targetSerialNumber?: string): Promise<boolean>;
  write(data: Uint8Array): Promise<void>;
  connectDevice(
    dto: GeneratePacketRequest,
    deviceInfo?: DeviceInfo,
    onProgress?: (progress: number, current: number, total: number) => void
  ): Promise<PacketToJsonResponse>;
  downloadLog(
    request: LogDownloadRequest,
    sessionId: string,
    onProgress?: (progress: number, current: number, total: number) => void,
    chunkUploadCallback?: (
      logId: string,
      serialNumber: string,
      chunkIndex: number,
      chunkData: Uint8Array
    ) => Promise<void>,
    chunkBatchUploadCallback?: (
      logId: string,
      serialNumber: string,
      chunks: Array<{
        chunk_index: number;
        chunk_data: number[];
        crc?: number;
      }>
    ) => Promise<void>,
    batchSize?: number
  ): Promise<LogDownloadResult>;
  cancelDownload(): Promise<void>;
  isConnected(): boolean;
  getSelectedDeviceName(): string;
  getConnectedDeviceType(): DeviceType | null;
  isDeviceTypeConnected(deviceType: DeviceType): boolean;
  clearBuffer(): void;
}

const BleContext = createContext<BleContextValue>({
  canUseBle: false,
  hasTriedAutoconnect: false,
  connectionState: 'disconnected',
  error: undefined,
  connectedDevice: undefined,
  connect: () => Promise.resolve(false),
  disconnect: () => {},
  scan: () => Promise.resolve([]),
  scanAndConnect: () => Promise.resolve(false),
  write: async () => {},
  connectDevice: async (
    _dto: GeneratePacketRequest,
    _deviceInfo?: { sensor_type?: Array<{ value: number; label: string }> }
  ): Promise<PacketToJsonResponse> => {
    return {} as PacketToJsonResponse;
  },
  downloadLog: async () => {
    return {
      success: false,
      log_id: '',
      file_type: 0,
      num_packets: 0,
      downloaded_packets: 0,
      chunks_stored: false,
      error_message: 'Not implemented',
    };
  },
  cancelDownload: async () => {},
  isConnected: () => false,
  getSelectedDeviceName: () => '',
  getConnectedDeviceType: () => null,
  isDeviceTypeConnected: () => false,
  clearBuffer: () => {},
});

const useBle = () => useContext(BleContext);

export {  useBle };

interface WebBleProviderProps {}

export { WebBleProvider };

// 패킷 저장 헬퍼 함수 (백그라운드, 에러 무시)
const savePacketInBackground = async (serialNumber: string, packet: number[]) => {
  try {
    if (!canSyncPacketToCloud()) {
      return;
    }

    await apiHelpers.post(
      'device.packet',
      {
        serial_number: serialNumber,
        packet,
      },
      {
        timeout: 30000, // 패킷 저장은 30초 타임아웃
      }
    );
  } catch (error) {
    // 백그라운드 저장 실패는 조용히 무시 (타임아웃 포함)
    // 로그 다운로드 중에는 많은 패킷이 저장되므로 타임아웃이 발생할 수 있음
    if ((error as any)?.code !== 'ECONNABORTED') {
      console.warn('[savePacketInBackground] Failed to save packet:', error);
    }
  }
};

const WebBleProvider = ({ children }: PropsWithChildren<WebBleProviderProps>) => {
  // 로그 다운로드용 fileType 상태 저장
  const fileTypeRef = useRef<number | null>(null);
  const { mutateAsync: generatePacket } = useGeneratePacket();
  const { mutateAsync: parsePacket } = useParsePacket();
  const { mutateAsync: generateMiniPacket } = useGenerateMiniPacket();
  const { mutateAsync: parseMiniPacket } = useParseMiniPacket();
  const { updateConnectedDevice, disconnectDevice, getAnyConnectedDevice } = useAppStore();
  const [canUseBle] = useState(() => 'bluetooth' in navigator);
  const [hasTriedAutoconnect] = useState(false); // BLE는 자동 연결을 시도하지 않음
  const [connectionState, setConnectionState] = useState<BleConnectionState>('disconnected');
  const [error, setError] = useState<string | undefined>(undefined);
  const [connectedDevice, setConnectedDevice] = useState<ConnectedDeviceInfo | undefined>(
    undefined
  );

  const adapterRef = useRef<WebBleAdapter | null>(null);
  const downloadHandlerRef = useRef<LogDownloadHandler | null>(null);

  const initializeHandler = () => {
    if (!adapterRef.current) {
      adapterRef.current = new WebBleAdapter();
    }
  };

  const connect = async (): Promise<boolean> => {
    return scanAndConnect();
  };

  const scanAndConnect = async (
    deviceFilter?: DeviceType | 'bell',
    targetSerialNumber?: string,
    namePrefix?: string
  ): Promise<boolean> => {
    if (!canUseBle) return false;
    if (connectionState === 'connecting') return false;

    // 이미 연결되어 있으면 성공 반환 (단, adapter가 null이 아닌 경우에만)
    if (isConnected() && adapterRef.current !== null) {
      setConnectionState('connected');
      return true;
    }

    // 연결 시작 전 상태 초기화

    setConnectionState('connecting');
    setError(undefined);

    try {
      initializeHandler();

      if (!adapterRef.current) {
        throw new I18nError(i18nKeys.config.error_bleAdapterInitFailed);
      }

      // Web BLE에서는 scanAndConnect 메서드 사용
      const adapterFilter = deviceFilter === 'bell' ? undefined : deviceFilter;
      // namePrefix가 없으면 기본값 'BELL' 사용
      const prefix = namePrefix || 'BELL';
      await adapterRef.current.scanAndConnect(adapterFilter, targetSerialNumber, prefix);

      // scanAndConnect가 성공적으로 resolve되었으므로 즉시 연결 상태 확인
      const adapterConnected = adapterRef.current?.getConnectionStatus() || false;
      if (!adapterConnected) {
        throw new I18nError(i18nKeys.config.error_bleConnectionFailedCheckDevice);
      }

      // 연결된 디바이스 정보 설정
      const deviceName = adapterRef.current?.getSelectedDeviceName() || '';
      const deviceType = detectDeviceTypeFromName(deviceName);

      if (deviceType) {
        setConnectedDevice({
          type: deviceType,
          name: deviceName,
        });
      }

      setConnectionState('connected');
      setError(undefined); // 성공 시 에러 상태도 초기화

      return true;
    } catch (error) {
      setConnectionState('error');

      // I18nError에서 i18n 키 추출
      const errorMessage = getI18nKey(error, i18nKeys.config.error_bleUnknownError);
      setError(errorMessage);
      return false;
    }
  };

  const scan = async (): Promise<BleDeviceInfo[]> => {
    if (!canUseBle) return [];

    try {
      initializeHandler();
      if (!adapterRef.current) {
        throw new I18nError(i18nKeys.config.error_bleAdapterInitFailed);
      }
      return await adapterRef.current.scan();
    } catch (error) {
      return [];
    }
  };

  const disconnect = () => {
    console.trace('[BLE] disconnect() call stack');

    try {
      // 연결된 기기의 serialNumber 찾기
      const connectedDevice = getAnyConnectedDevice();
      const serialNumber =
        connectedDevice?.serialNumber ||
        connectedDevice?.deviceData?.serial_number ||
        connectedDevice?.serialNumber;

      if (adapterRef.current) {
        adapterRef.current.disconnect();
        // 어댑터 참조 완전히 제거 (재스캔을 위해 필수)
        adapterRef.current = null;
      }

      setConnectionState('disconnected');
      setError(undefined);
      setConnectedDevice(undefined); // 연결된 디바이스 정보 초기화

      // 전역 상태에서 연결 해제
      if (serialNumber) {
        disconnectDevice(serialNumber, 'ble');
      }
    } catch (error) {
      setConnectionState('error');
      setError(error instanceof Error ? error.message : String(error));
    }
  };

  const write = async (data: Uint8Array): Promise<void> => {
    if (!adapterRef.current) {
      throw new I18nError(i18nKeys.config.error_bleNotInitialized);
    }
    if (connectionState !== 'connected') {
      throw new I18nError(i18nKeys.config.error_bleNotConnected);
    }
    await adapterRef.current.write(data);
  };

  const isConnected = (): boolean => {
    // 실제 BLE 어댑터의 연결 상태도 확인
    const adapterConnected = adapterRef.current?.getConnectionStatus() || false;
    const stateConnected = connectionState === 'connected';

    // 둘 중 하나라도 연결되어 있으면 연결된 것으로 간주
    return adapterConnected || stateConnected;
  };

  const getSelectedDeviceName = (): string => {
    return adapterRef.current?.getSelectedDeviceName() || '';
  };

  const getConnectedDeviceType = (): DeviceType | null => {
    return connectedDevice?.type || null;
  };

  const isDeviceTypeConnected = (deviceType: DeviceType): boolean => {
    return connectedDevice?.type === deviceType && connectionState === 'connected';
  };

  const clearBuffer = (): void => {
    if (adapterRef.current) {
      const adapterAny = adapterRef.current as any;
      if (adapterAny?.clearBuffer) {
        adapterAny.clearBuffer();
      }
    }
  };

  // PacketApiAdapter 생성 헬퍼
  const createPacketApi = (isMiniDevice: boolean): PacketApiAdapter => ({
    generatePacket: isMiniDevice ? generateMiniPacket : generatePacket,
    parsePacket: isMiniDevice ? parseMiniPacket : parsePacket,
  });

  const connectDevice = async (
    dto: GeneratePacketRequest,
    deviceInfo?: DeviceInfo,
    onProgress?: (progress: number, current: number, total: number) => void
  ): Promise<PacketToJsonResponse> => {
    // BLE adapter 초기화 확인
    if (!adapterRef.current) {
      console.error(
        ' [BLE.connectDevice] Adapter not initialized. connectionState:',
        connectionState
      );
      // 연결 상태를 에러로 변경
      setConnectionState('error');
      setError('BLE adapter not initialized');
      throw new I18nError(i18nKeys.config.error_bleNotInitialized);
    }

    // BLE 연결 상태 확인 (실제 어댑터 상태도 함께 확인)
    const adapterConnected = adapterRef.current?.getConnectionStatus() || false;
    const stateConnected = connectionState === 'connected';

    if (!adapterConnected && !stateConnected) {
      console.error(
        ' [BLE.connectDevice] Not connected. adapterConnected:',
        adapterConnected,
        'stateConnected:',
        stateConnected
      );
      setConnectionState('error');
      throw new I18nError(i18nKeys.config.error_bleNotConnectedDesc);
    }

    try {
      // 디바이스 타입 확인 (BELLMINI_로 시작하면 mini)
      const deviceName = getSelectedDeviceName();
      const isMiniDevice = deviceName.toUpperCase().startsWith('BELLMINI_');

      // Class 16 요청인지 확인
      const isClass16 = dto.class_id === 16 && dto.function_id === 1;

      // 로그 다운로드 모드: function_id 1 (로그 정보 요청) 또는 Mini 청크 요청/응답 function_id
      const isLogDownloadMode =
        isClass16 ||
        (isMiniDevice &&
          (dto.function_id === 0x03 || // Mini Datalog 청크 요청
            dto.function_id === 0x04 || // Mini Datalog 청크 응답
            dto.function_id === 0x05 || // Mini Event 청크 요청
            dto.function_id === 0x06 || // Mini Event 청크 응답
            dto.function_id === 0x07 || // Mini Cal/Bump 청크 요청
            dto.function_id === 0x08 || // Mini Cal/Bump 청크 응답
            dto.function_id === 0x09 || // Mini Sensor Config 청크 요청
            dto.function_id === 0x0a || // Mini Sensor Config 청크 응답
            dto.function_id === 0x18)); // Mini 버스트 Datalog 응답

      // class 16, function 1 요청이면 payload에서 fileType 추출 및 저장
      let fileType: number | null = null;
      if (dto.class_id === 16 && dto.function_id === 1 && dto.json_payload) {
        const payloadFileType = dto.json_payload.file_type;
        fileType = typeof payloadFileType === 'number' ? payloadFileType : null;
        if (typeof fileType === 'number') {
          fileTypeRef.current = fileType;
        }
      }

      // 패킷 API 어댑터 생성
      const packetApi = createPacketApi(isMiniDevice);

      // 적절한 Mode 선택 및 실행
      const mode = isClass16
        ? new BurstMode(packetApi, (serialNumber, packet) =>
            savePacketInBackground(serialNumber, packet)
          )
        : new SinglePacketMode(packetApi, (serialNumber, packet) =>
            savePacketInBackground(serialNumber, packet)
          );

      // 통신 컨텍스트 생성
      const context = {
        deviceInfo: deviceInfo || null,
        isMiniDevice,
        dto,
        fileType,
        isLogDownloadMode,
        onProgress,
      };

      // Mode 실행
      const result = await mode.execute(adapterRef.current, context);

      if (!result.success || !result.response) {
        throw new Error('통신 실패');
      }

      const jsonResponse = result.response;

      // 기기 정보 요청(class_id: 1, function_id: 1)인 경우 전역 상태에 저장
      if (dto.class_id === 1 && dto.function_id === 1 && jsonResponse.json) {
        const deviceData = jsonResponse.json as any;

        if (deviceData.serial_number) {
          // 다른 serialNumber의 모든 연결 해제 (다른 기기로 전환 시)
          const connectedDevice = getAnyConnectedDevice();
          const newSerialNumber = deviceData.serial_number;

          if (connectedDevice?.serialNumber && connectedDevice.serialNumber !== newSerialNumber) {
            // 모든 연결 타입 해제
            const serialNumber = connectedDevice.serialNumber;
            connectedDevice.connections.forEach(conn => {
              if (conn.isConnected) {
                disconnectDevice(serialNumber, conn.type);
              }
            });
          } else if (
            connectedDevice?.serialNumber &&
            connectedDevice.serialNumber === newSerialNumber &&
            connectedDevice?.connections.some(c => c.type === 'serial' && c.isConnected)
          ) {
            // 같은 기기지만 다른 연결 타입(Serial)이 있으면 해제
            const serialNumber = connectedDevice.serialNumber;
            disconnectDevice(serialNumber, 'serial');
          }

          try {
            updateConnectedDevice(deviceData.serial_number, 'ble', {
              name: deviceData.serial_number,
              serialNumber: deviceData.serial_number,
              ...deviceData,
            });
          } catch (error) {
            console.error('[BLE Provider] 기기 정보 저장 실패:', error);
          }
        } else {
          console.warn('[BLE Provider] serial_number 없음:', deviceData);
        }
      }

      return jsonResponse;
    } catch (error) {
      // 에러 발생 시에도 모드 복귀
      if (adapterRef.current) {
        adapterRef.current.setSinglePacketMode();
      }
      console.error('[BLE connectDevice] 송수신 실패:', error);
      throw error;
    }
  };

  /**
   * 로그 다운로드 전용 함수
   * Class 16 통신을 담당하며, 청크를 파싱하지 않고 세션에 저장
   * @param chunkUploadCallback 스트리밍 업로드 콜백 (청크를 받는 즉시 전송)
   */
  const downloadLog = async (
    request: LogDownloadRequest,
    sessionId: string,
    onProgress?: (progress: number, current: number, total: number) => void,
    chunkUploadCallback?: (
      logId: string,
      serialNumber: string,
      chunkIndex: number,
      chunkData: Uint8Array
    ) => Promise<void>,
    chunkBatchUploadCallback?: (
      logId: string,
      serialNumber: string,
      chunks: Array<{
        chunk_index: number;
        chunk_data: number[];
        crc?: number;
      }>
    ) => Promise<void>,
    batchSize?: number
  ): Promise<LogDownloadResult> => {
    if (!adapterRef.current) {
      throw new I18nError(i18nKeys.config.error_bleNotInitialized);
    }

    // BLE 연결 상태 확인
    const adapterConnected = adapterRef.current?.getConnectionStatus() || false;
    const stateConnected = connectionState === 'connected';

    if (!adapterConnected && !stateConnected) {
      throw new I18nError(i18nKeys.config.error_bleNotConnectedDesc);
    }

    // 디바이스 타입 확인
    const deviceName = getSelectedDeviceName();
    const isMiniDevice = deviceName.toUpperCase().startsWith('BELLMINI_');

    // 패킷 API 어댑터 생성
    const packetApi = {
      generatePacket: isMiniDevice ? generateMiniPacket : generatePacket,
      parsePacket: isMiniDevice ? parseMiniPacket : parsePacket,
    };

    // LogDownloadHandler 생성 (배치 콜백 포함)
    const handler = new LogDownloadHandler(
      adapterRef.current as any,
      packetApi as any,
      isMiniDevice,
      chunkUploadCallback,
      chunkBatchUploadCallback,
      batchSize
    );

    // 핸들러 참조 저장 (취소 시 사용)
    downloadHandlerRef.current = handler;

    try {
      // 로그 다운로드 실행
      return await handler.downloadLog(request, sessionId, onProgress);
    } finally {
      // 다운로드 완료 또는 실패 시 핸들러 참조 제거
      downloadHandlerRef.current = null;
    }
  };

  /**
   * 진행 중인 로그 다운로드 취소
   * 버스트 모드 중단 명령(0x16)을 기기에 전송
   */
  const cancelDownload = async (): Promise<void> => {
    // If handler exists, use it (downloadLog case)
    if (downloadHandlerRef.current) {
      try {
        const success = await downloadHandlerRef.current.cancelDownload();

        if (success) {
        } else {
          console.warn('[BLE] Download cancelled but stop command may have failed');
        }
        downloadHandlerRef.current = null;
      } catch (error) {
        console.error(' [BLE] Failed to cancel download via handler:', error);
        throw error;
      }
      return;
    }

    // If no handler, send 0x16 directly via adapter
    if (!adapterRef.current) {
      console.warn('[BLE] No adapter available');
      return;
    }

    const deviceName = getSelectedDeviceName();
    const isMiniDevice = deviceName.toUpperCase().startsWith('BELLMINI_');
    const genPacket = isMiniDevice ? generateMiniPacket : generatePacket;
    const parsePkt = isMiniDevice ? parseMiniPacket : parsePacket;

    try {
      // Generate stop burst mode packet (Class 16, Function 0x16)
      const stopPacket = await genPacket({
        class_id: 16,
        function_id: 0x16,
        json_payload: {},
      });

      // Send stop command via adapter (with retry)
      if (adapterRef.current.stopBurstMode) {
        const maxRetries = 3;
        let success = false;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            const response = await adapterRef.current.stopBurstMode(
              new Uint8Array(stopPacket.packet)
            );

            // Parse response (0x17)
            const parsed = await parsePkt({
              class_id: 16,
              function_id: 0x17,
              packet: Array.from(response),
            });

            const result = parsed.json?.Result || 0;
            if (result === 1) {
              success = true;
              break;
            } else if (result === 0) {
              // Result=0: Device may not be in burst mode, but that's OK for cancel
              success = true;
              break;
            } else {
              console.warn(`[BLE] Stop burst mode failed: Result=${result} (attempt ${attempt})`);
              if (attempt < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms before retry
              }
            }
          } catch (error) {
            console.error(` [BLE] Error on attempt ${attempt}:`, error);
            if (attempt < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        }

        if (!success) {
          console.warn('[BLE] All stop burst mode attempts failed');
        }
      } else {
        console.warn('[BLE] Adapter does not support stopBurstMode');
      }
    } catch (error) {
      console.error(' [BLE] Failed to send 0x16 packet:', error);
      throw error;
    }
  };

  return (
    <BleContext.Provider
      value={{
        canUseBle,
        hasTriedAutoconnect,
        connectionState,
        error,
        connectedDevice,
        connect,
        disconnect,
        scan,
        scanAndConnect,
        write,
        connectDevice,
        downloadLog,
        cancelDownload,
        isConnected,
        getSelectedDeviceName,
        getConnectedDeviceType,
        isDeviceTypeConnected,
        clearBuffer,
      }}
    >
      {children}
    </BleContext.Provider>
  );
};

